import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, filter, isEqual, keys, map, mapValues, omit, sortBy, values } from 'lodash';
import { Subject, combineLatest, takeUntil, map as rxMap, of, catchError, switchMap } from 'rxjs';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControlOptions, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import {
  Gender,
  UserType,
  StateType,
  SocialType,
  CountryType,
  LanguageType,
  AcademicLevel,
  MaritalStatus,
  ZoneTypesEnum,
  CorporateUserType,
  LoyaltySettingsType,
  GenerateS3SignedUrlGQL,
} from '@sifca-monorepo/widget-generator';
import { AmazonS3Helper, FormHelper, ValidationHelper } from '@diktup/frontend/helpers';
import { MobileAuthService, MobileProfileService, MobileReputationService, MobileThemeService, ToasterService } from '@sifca-monorepo/clients';

import { AWS_CREDENTIALS } from '../../environments/environment';

interface ClientGeoData {
  status: string;
  lat: number;
  lon: number;
  country?: string;
  city?: string;
  query?: string;
}
@Component({
  template: '',
})
export class MobileEditProfileComponent implements OnDestroy {
  private readonly geoZoom = 12;
  private unsubscribeAll: Subject<any> = new Subject<any>();

  @ViewChild('leafletMap') protected leafletMapEl?: ElementRef<HTMLDivElement>;

  // Leaflet interop differs between dev/prod bundlers (CJS/ESM). Keep flexible.
  leafletLib?: any;
  map?: import('leaflet').Map;
  marker?: import('leaflet').CircleMarker;
  // GEOLOCATION
  position?: { lat: number; lng: number };
  mapCenter = { lat: 33.886917, lng: 9.537499 };
  mapZoom = 4;

  isIos: boolean;
  loading = true;
  genders: string[];
  initialValues: any;
  userForm: FormGroup;
  states: StateType[];
  activeModal = 'info';
  media: SocialType[];
  isButtonDisabled = true;
  maritalStatus: string[];
  countries: CountryType[];
  languages: LanguageType[];
  passwordForm: FormGroup;
  isPwdButtonDisabled = true;
  originalStates: StateType[];
  originalCountries: CountryType[];
  user: UserType | CorporateUserType;
  dark$ = this.themeService.darkMode$;
  loyaltySettings: LoyaltySettingsType;
  interestControl = new FormControl('');
  levels: string[] = values(AcademicLevel);
  states$ = this.profileService?.infiniteStates$;
  currentLevel$ = this.reputationService.currentLevel$;
  widgetSettings$ = this.reputationService.widgetSettings$;

  constructor(
    protected platform: Platform,
    protected formBuilder: FormBuilder,
    protected translate: TranslateService,
    protected amazonS3Helper: AmazonS3Helper,
    protected themeService: MobileThemeService,
    protected authService: MobileAuthService,
    protected toasterService: ToasterService,
    protected profileService: MobileProfileService,
    protected cd: ChangeDetectorRef,
    protected reputationService: MobileReputationService,
    protected generateS3SignedUrlGQL: GenerateS3SignedUrlGQL,
  ) {
    this.isIos = this.platform.is('ios');
    this.maritalStatus = filter(Object.values(MaritalStatus), (status) => status !== MaritalStatus.PREFER_NOT_TO_SAY);
    this.genders = filter(Object.values(Gender), (status) => status !== Gender.PREFER_NOT_TO_SAY);
    setTimeout(() => {
      this.loading = false;
    }, 1000);
    this.reputationService.loyaltySettings$
      .pipe(
        takeUntil(this.unsubscribeAll),
        rxMap((loyaltySettings) => {
          this.loyaltySettings = loyaltySettings;
          this.cd.markForCheck();
        }),
      )
      .subscribe();

    this.passwordForm = this.formBuilder.group({
      pwd: this.formBuilder.group(
        {
          currentPassword: ['', Validators.required],
          password: ['', Validators.required],
          confirmPassword: ['', Validators.required],
        },
        { validators: [ValidationHelper.matchingFields('password', 'confirmPassword')] } as AbstractControlOptions,
      ),
    });
    const initPwdVal = this.passwordForm.value;
    this.passwordForm.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((values) => {
      this.isPwdButtonDisabled = isEqual(initPwdVal, values);
    });
    this.profileService.languages$.pipe(takeUntil(this.unsubscribeAll)).subscribe((languages) => {
      const filteredLanguages = filter(
        languages,
        (lang) =>
          lang.name === 'Arabic (Saudi Arabia)' ||
          lang.name === 'English (United States)' ||
          lang.name === 'French (Standard)' ||
          lang.name === 'German (Standard)',
      );

      this.languages = filteredLanguages.map((lang) => ({
        ...lang,
        isChecked: this.user?.languages?.some((userLang) => userLang?.id === lang?.id),
      }));
    });
    this.authService.currentUser$.pipe(takeUntil(this.unsubscribeAll)).subscribe((user) => {
      this.user = user;
      this.position = {
        lng: user?.residentialAddress?.[0]?.location?.coordinates?.[0],
        lat: user?.residentialAddress?.[0]?.location?.coordinates?.[1],
      };
      this.userForm = this.formBuilder.group({
        socialMedia: this.formBuilder.group({
          facebook: this.formBuilder.group({
            value: [''],
            name: [''],
          }),
          instagram: this.formBuilder.group({
            value: [''],
            name: [''],
          }),
          twitter: this.formBuilder.group({
            value: [''],
            name: [''],
          }),
          youtube: this.formBuilder.group({
            value: [''],
            name: [''],
          }),
          tikTok: this.formBuilder.group({
            value: [''],
            name: [''],
          }),
          linkedin: this.formBuilder.group({
            value: [''],
            name: [''],
          }),
          pinterest: this.formBuilder.group({
            value: [''],
            name: [''],
          }),
        }),
        phone: this.formBuilder.group({
          countryCode: [{ value: this.user?.phone?.countryCode, disabled: user?.phone?.number ? true : false }],
          number: [{ value: this.user?.phone?.number, disabled: user?.phone?.number ? true : false }, Validators.pattern(/^\d{8}$/)],
        }),
        email: [{ value: this.user?.email, disabled: user?.email ? true : false }, Validators.email],
        gender: [this.user?.gender || undefined],
        dateOfBirth: [this.user?.dateOfBirth || ''],
        lastName: [this.user?.lastName || ''],
        about: [this.user?.about || ''],
        languages: [this.user?.languages?.length ? map(this.user?.languages, 'id') : []],
        firstName: [this.user?.firstName || ''],
        interests: [this.user?.interests || []],
        hobbies: [this.user?.hobbies || []],
        maritalStatus: [this.user?.maritalStatus || undefined],
        residentialAddress: this.formBuilder.group({
          address: [this.user?.residentialAddress?.length ? this.user?.residentialAddress?.[0]?.address : ''],
          postCode: [this.user?.residentialAddress?.length ? this.user?.residentialAddress?.[0]?.postCode : ''],
          city: [this.user?.residentialAddress?.length ? this.user?.residentialAddress?.[0]?.city : undefined],
          country: [this.user?.residentialAddress?.length ? this.user?.residentialAddress?.[0]?.country?.id : undefined],
          state: [this.user?.residentialAddress?.length ? this.user?.residentialAddress?.[0]?.state?.id : null],
          states: [[]],
          location: this.formBuilder.group({
            type: [this.user?.residentialAddress?.length ? this.user?.residentialAddress?.[0]?.location?.type : ZoneTypesEnum.POINT],
            coordinates: [this.user?.residentialAddress?.length ? this.user?.residentialAddress?.[0]?.location?.coordinates : []],
          }),
        }),
      });
      const residentialAddressControl = this.userForm.get('residentialAddress');
      if (residentialAddressControl.get('country').value) {
        this.profileService.findStatesByCountryPagination(residentialAddressControl.get('country').value).subscribe((res) => {
          residentialAddressControl.get('states').patchValue(res);
          this.isButtonDisabled = true;
        });
      }
      if (this.media) {
        this.patchSocialValues();
        this.isButtonDisabled = true;
      }
      this.profileService.socialMedia$.pipe(takeUntil(this.unsubscribeAll)).subscribe((res) => {
        this.media = res;
        this.patchSocialValues();
        this.isButtonDisabled = true;
        this.cd.markForCheck();
      });
      this.initialValues = this.userForm.value;
      this.userForm.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((values) => {
        this.isButtonDisabled = isEqual(this.initialValues, values);
      });
      if (user) {
        setTimeout(() => {
          this.applyGeoDataCenter();
          this.initLeafletMap();
          this.syncLeafletToPosition();
        });
      }
      // When position becomes available, sync marker/map.
    });
    combineLatest([
      this.profileService.findCountriesPagination(),
      this.profileService.findSocialsPagination(),
      this.profileService.getLanguages(),
    ]).subscribe(([res, result]) => {
      if (res) {
        this.countries = sortBy(res, ['name']);
        this.originalCountries = sortBy(res, ['name']);
        this.media = result.objects;
        this.cd.markForCheck();
      }
    });

    this.profileService.infiniteStates$.pipe(takeUntil(this.unsubscribeAll)).subscribe((states) => {
      this.states = sortBy(states, ['name']);
      this.originalStates = sortBy(states, ['name']);
    });
  }

  onSwitchTab(activeModal?: string): void {
    this.activeModal = activeModal;
    if (activeModal === 'address') {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 200);
    }
    this.cd.markForCheck();
  }

  get isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  async getLeafletLib(): Promise<any | null> {
    if (!this.isBrowser) return null;
    if (this.leafletLib) return this.leafletLib;
    const leafletModule = await import('leaflet');
    this.leafletLib = (leafletModule as any)?.default ?? leafletModule;
    return this.leafletLib;
  }

  async initLeafletMap(): Promise<void> {
    if (!this.isBrowser) return;
    if (this.map) {
      this.invalidateLeafletSize();
      this.syncLeafletToPosition();
      return;
    }
    const container = this.leafletMapEl?.nativeElement;
    if (!container) return;
    const L = await this.getLeafletLib();
    if (!L || typeof L?.map !== 'function') {
      return;
    }
    this.stopSliderDragOnMapContainer(container, L);

    const defaultLat = 33.886917;
    const defaultLng = 9.537499;
    const hasPosition = !!(this.position?.lat && this.position?.lng);
    const lat = hasPosition ? this.position.lat : defaultLat;
    const lng = hasPosition ? this.position.lng : defaultLng;
    const zoom = hasPosition ? 12 : 4;

    this.map = L.map(container, {
      zoomControl: true,
      attributionControl: true,
    }).setView([lat, lng], zoom);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(this.map);

    this.map.on('click', (e: any) => this.pickAddress(e));
    this.map.on('moveend', () => this.invalidateLeafletSize());

    setTimeout(() => {
      this.invalidateLeafletSize();
      this.syncLeafletToPosition();
    }, 50);
  }

  private applyGeoDataCenter(): void {
    if (this.position?.lat && this.position?.lng) return;

    const coords = this.userForm.get(['residentialAddress', 'location', 'coordinates'])?.value;
    if (Array.isArray(coords) && coords.length === 2 && coords[0] && coords[1]) {
      this.mapCenter = { lat: coords[1], lng: coords[0] };
      this.mapZoom = this.geoZoom;
      return;
    }

    const geoData = this.readGeoData();
    if (geoData?.lat != null && geoData?.lon != null) {
      this.mapCenter = { lat: geoData.lat, lng: geoData.lon };
      this.mapZoom = this.geoZoom;
    }
  }

  private readGeoData(): ClientGeoData | null {
    if (!this.isBrowser) return null;
    try {
      const raw = localStorage.getItem('geoData');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as ClientGeoData;
      return parsed?.status === 'success' || (parsed?.lat != null && parsed?.lon != null) ? parsed : null;
    } catch {
      return null;
    }
  }

  private stopSliderDragOnMapContainer(container: HTMLElement, L: any): void {
    const stop = (e: Event) => e.stopPropagation();
    container.addEventListener('pointerdown', stop);
    container.addEventListener('mousedown', stop);
    container.addEventListener('touchstart', stop, { passive: true } as any);
  }

  invalidateLeafletSize(): void {
    try {
      this.map?.invalidateSize({ pan: false });
    } catch {
      // no-op
    }
  }

  syncLeafletToPosition(): void {
    if (!this.position?.lat || !this.position?.lng) return;
    this.setLeafletMarker(this.position.lat, this.position.lng, false);
  }

  private setLeafletMarker(lat: number, lng: number, recenter: boolean): void {
    const L = this.leafletLib;
    if (!L || !this.map) return;
    if (!this.marker) {
      // Use a circle marker to avoid Leaflet image asset configuration.
      this.marker = L.circleMarker([lat, lng], {
        radius: 9,
        color: '#f97316',
        weight: 2,
        fillColor: '#f97316',
        fillOpacity: 0.85,
      }).addTo(this.map);
    } else {
      this.marker.setLatLng([lat, lng]);
    }

    if (recenter) {
      this.map.setView([lat, lng], Math.max(this.map.getZoom(), 12), { animate: true });
    }
  }

  updatePassword() {
    if (!this.passwordForm) {
      return;
    }
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      this.cd.markForCheck();
      return;
    }

    this.isPwdButtonDisabled = true;
    const currentPassword = this.passwordForm.get(['pwd', 'currentPassword'])?.value;
    const newPassword = this.passwordForm.get(['pwd', 'password'])?.value;

    this.authService
      .updateCurrentUserPassword(currentPassword, newPassword)
      .pipe(
        catchError((error) => {
          const exception = error?.graphQLErrors?.[0]?.message;
          this.toasterService.error(exception || this.translate.instant('STH_WENT_WRONG'));
          this.cd.markForCheck();
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.toasterService.success(this.translate.instant('PROFILE_INFO_UPDATED'));
          this.passwordForm.reset({
            pwd: {
              currentPassword: '',
              password: '',
              confirmPassword: '',
            },
          });
        }
        this.cd.markForCheck();
      });
  }

  onInterestsChange(selected: any): void {
    this.setMultiSelectValue('interests', selected);
  }

  onHobbiesChange(selected: any): void {
    this.setMultiSelectValue('hobbies', selected);
  }

  private setMultiSelectValue(controlName: 'hobbies' | 'interests', selected: any): void {
    const control = this.userForm?.get(controlName);
    if (!control) {
      return;
    }
    const values: any[] = Array.isArray(selected) ? selected : [selected];
    control.setValue(values.filter((value) => value !== null && value !== undefined && value !== ''));
    this.cd.markForCheck();
  }

  modalSuccess() {
    const message = this.translate.instant('PROFILE_INFO_UPDATED');
    this.toasterService.success(message);
  }

  patchSocialValues() {
    this.userForm.get('socialMedia').patchValue({
      facebook: {
        value: this.user?.socialMedia?.find((item) => item.name.code === 'FB')?.value || '',
        name: this.media?.find((item) => item.code === 'FB')?.id || '',
      },
      instagram: {
        value: this.user?.socialMedia?.find((item) => item.name.code === 'IG')?.value || '',
        name: this.media?.find((item) => item.code === 'IG')?.id || '',
      },
      twitter: {
        value: this.user?.socialMedia?.find((item) => item.name.code === 'TT')?.value || '',
        name: this.media?.find((item) => item.code === 'TT')?.id || '',
      },
      youtube: {
        value: this.user?.socialMedia?.find((item) => item.name.code === 'YT')?.value || '',
        name: this.media?.find((item) => item.code === 'YT')?.id || '',
      },
      tikTok: {
        value: this.user?.socialMedia?.find((item) => item.name.code === 'TK')?.value || '',
        name: this.media?.find((item) => item.code === 'TK')?.id || '',
      },
      linkedin: {
        value: this.user?.socialMedia?.find((item) => item.name.code === 'LI')?.value || '',
        name: this.media?.find((item) => item.code === 'LI')?.id || '',
      },
      pinterest: {
        value: this.user?.socialMedia?.find((item) => item.name.code === 'PR')?.value || '',
        name: this.media?.find((item) => item.code === 'PR')?.id || '',
      },
    });
  }

  upload(): void {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.name = 'fileUpload';
    fileInput.id = 'fileUpload';
    fileInput.style.position = 'fixed';
    fileInput.style.top = '-9999px';
    fileInput.style.left = '-9999px';
    fileInput.style.opacity = '0';

    const cleanup = () => {
      if (document.body.contains(fileInput)) {
        document.body.removeChild(fileInput);
      }
    };

    fileInput.onchange = async () => {
      const file = fileInput.files?.[0];
      cleanup();
      if (!file) return;

      const posId = localStorage.getItem('posId');
      const timestamp = Date.now();
      const fileName = `${posId}_${timestamp}_${file.name}`;
      this.generateS3SignedUrlGQL
        .fetch({
          fileName,
          fileType: file.type,
        })
        .subscribe(async (res) => {
          const picture = await this.amazonS3Helper.uploadS3AwsWithSignature(
            res.data.generateS3SignedUrl.message,
            file,
            fileName,
            AWS_CREDENTIALS.storage,
            AWS_CREDENTIALS.region,
          );
          const input = {
            picture: {
              path: picture.path,
              baseUrl: picture.baseUrl,
            },
          };
          this.updateCureentUser(input);
          this.cd.markForCheck();
        });
    };

    // iOS Safari requires the input to be in the DOM and the click to happen
    // synchronously within a user gesture for the file picker to open.
    document.body.appendChild(fileInput);
    fileInput.click();
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (!/^\d$/.test(event.key) && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  modalError(text?: string) {
    this.toasterService.error(text);
  }

  updateCureentUser(input) {
    if (!input || !keys(input).length) return;
    this.profileService
      .updateCurrentUser(input)
      .pipe(
        catchError((error) => {
          const exception = error?.graphQLErrors?.[0]?.message;
          const text = exception?.includes('unique_phone_number')
            ? this.translate.instant('PHONE_NUMBER_ALREADY_EXISTS')
            : this.translate.instant('STH_WENT_WRONG');
          this.modalError(text);
          this.cd.markForCheck();
          return of(null);
        }),
        switchMap((res) => {
          if (res) {
            this.modalSuccess();
          }
          if (this.loyaltySettings?.profileComplete?.enable) {
            return this.profileService.getProfileCompletnessProgress();
          }
          return of(null);
        }),
      )
      .subscribe(() => {
        this.cd.markForCheck();
      });
  }

  pickAddress(event: any) {
    if (event?.latlng) {
      const lat = event.latlng.lat;
      const lng = event.latlng.lng;
      this.position = { lat, lng };
      this.userForm.get(['residentialAddress', 'location', 'coordinates'])?.patchValue([lng, lat]);
      this.setLeafletMarker(lat, lng, true);
      return;
    }
  }

  save() {
    this.isButtonDisabled = true;
    const residentialAddress = {
      ...FormHelper.getDifference(
        omit(this.initialValues.residentialAddress, 'states', 'location'),
        omit(this.userForm.get('residentialAddress').value, 'states', 'location'),
      ),
      ...(this.position?.lng &&
      this.initialValues?.residentialAddress?.location?.coordinates?.[0] !== this.userForm.get('residentialAddress').value.location?.coordinates?.[0]
        ? {
            location: {
              coordinates: [this.position.lng, this.position.lat],
              type: ZoneTypesEnum.POINT,
            },
          }
        : {}),
    };
    const socialMediaFormValue = this.userForm?.get('socialMedia').value;
    const socialPlatforms = ['facebook', 'instagram', 'twitter', 'youtube', 'tikTok', 'linkedin', 'pinterest'];
    const socialMedia = socialPlatforms
      .filter((platform) => socialMediaFormValue[platform]?.value !== '')
      .map((platform) => ({
        value: socialMediaFormValue[platform]?.value,
        name: socialMediaFormValue[platform]?.name,
      }));
    const input: any = {
      ...FormHelper.getDifference(
        omit(this.initialValues, 'residentialAddress', 'phone', 'socialMedia', 'interests', 'hobbies'),
        omit(this.userForm.value, 'residentialAddress', 'phone', 'socialMedia', 'interests', 'hobbies'),
      ),
      ...(keys(residentialAddress)?.length
        ? {
            residentialAddress: {
              ...FormHelper.getNonEmptyValues(omit(this.userForm.get('residentialAddress').value, 'states', 'location')),
              ...(this.position?.lng
                ? {
                    location: this.position?.lng
                      ? {
                          coordinates: [this.position.lng, this.position.lat],
                          type: ZoneTypesEnum.POINT,
                        }
                      : {},
                  }
                : {}),
            },
          }
        : {}),

      ...(isEqual(extractSocialMediaValues(this.initialValues.socialMedia), extractSocialMediaValues(this.userForm.get('socialMedia').value))
        ? {}
        : { socialMedia: socialMedia }),
      ...(isEqual(
        (this.initialValues.interests ? cloneDeep(this.initialValues.interests) : []).sort(),
        (this.userForm?.value?.interests?.length ? cloneDeep(this.userForm?.value?.interests) : []).sort(),
      )
        ? {}
        : { interests: this.userForm.value.interests }),
      ...(this.initialValues.phone?.countryCode === this.userForm.value.phone?.countryCode &&
      this.initialValues.phone?.number === this.userForm.value.phone?.number
        ? {}
        : { phone: this.userForm.value.phone }),
      ...(isEqual(
        (this.initialValues.hobbies ? cloneDeep(this.initialValues.hobbies) : []).sort(),
        (this.userForm?.value?.hobbies?.length ? cloneDeep(this.userForm?.value?.hobbies) : []).sort(),
      )
        ? {}
        : { hobbies: this.userForm.value.hobbies }),
    };

    function extractSocialMediaValues(socialMedia: any): any {
      return mapValues(socialMedia, (entry) => entry?.value || '');
    }
    this.updateCureentUser(input);
  }

  patchSocialMediaValues() {
    const socialMediaControls = this.userForm.get('socialMedia');
    socialMediaControls.get('facebook').patchValue({
      value: this.user?.socialMedia?.find((item) => item.name.code === 'FB')?.value || '',
      name: this.media?.find((item) => item.code === 'FB')?.id || '',
    });
    const instagramValue = this.user?.socialMedia?.find((item) => item.name.code === 'IG')?.value || '';
    const instagramId = this.media?.find((item) => item.code === 'IG')?.id || '';
    socialMediaControls.get('instagram').patchValue({ value: instagramValue, name: instagramId });
  }

  onSearchCountry(query: string) {
    this.filterCountries(query);
  }

  filterStates(searchTerm: string) {
    this.states = filter(this.originalStates, (state) => state.name?.toLowerCase().includes(searchTerm !== null ? searchTerm?.toLowerCase() : ''));
  }

  filterCountries(searchTerm: string) {
    this.countries = filter(this.originalCountries, (country) =>
      country.name?.toLowerCase().includes(searchTerm !== null ? searchTerm?.toLowerCase() : ''),
    );
  }

  onChangeResidentialCountry(country: CountryType) {
    this.userForm.get(['residentialAddress', 'state']).patchValue(null);
    if (!country) {
      this.userForm.get(['residentialAddress', 'country']).patchValue(undefined);
      return;
    }
    this.profileService.findStatesByCountryPagination(country?.id).subscribe((res) => {
      if (res) {
        this.userForm.get(['residentialAddress', 'states']).patchValue(res);
      }
    });
  }

  ngOnDestroy(): void {
    try {
      this.map?.off();
      this.map?.remove();
    } catch {
      // no-op
    } finally {
      this.map = undefined;
      this.marker = undefined;
    }

    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }
}
